import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type SelectOption = { value: string; label: string; disabled?: boolean };

/** Themed dropdown that replaces native <select> — the native option list can't be
 *  styled (OS-drawn), so we render our own: rounded, theme surface, accent-highlighted
 *  selection. Closes on outside-click or Escape. The menu is portaled to <body> via
 *  fixed positioning so it never gets clipped by a scroll container or modal. */
export function Select({
  value, onChange, options, placeholder, className, style, disabled, ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);

  const place = () => {
    const b = wrapRef.current?.getBoundingClientRect();
    if (!b) return;
    const below = window.innerHeight - b.bottom;
    const wantUp = below < 240 && b.top > below;
    setPos({ left: b.left, top: wantUp ? b.top : b.bottom + 4, width: b.width });
  };
  useLayoutEffect(() => { if (open) place(); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!wrapRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const cur = options.find((o) => o.value === value);
  return (
    <div className={`fx-select${className ? ` ${className}` : ''}`} ref={wrapRef} style={style}>
      <button
        type="button"
        className="fx-select-btn"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className={`fx-select-val${cur ? '' : ' fx-select-ph'}`}>{cur ? cur.label : (placeholder || 'Select…')}</span>
        <svg className="fx-select-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && pos && createPortal(
        <div
          className="fx-select-list"
          role="listbox"
          ref={menuRef}
          style={{ position: 'fixed', left: pos.left, top: pos.top, minWidth: pos.width }}
        >
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              className={`fx-select-opt${o.value === value ? ' is-sel' : ''}`}
              disabled={o.disabled}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
